import {useCallback, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
    contentTypeRequestFailed,
    contentTypeRequestStarted,
    contentTypeRequestSucceeded,
    invalidateContentType,
    removeContentTypeItem,
    selectContentTypeEntry,
    upsertContentTypeItem,
} from "./content-type-data-slice";
import {
    createAdminEntity,
    deleteAdminEntity,
    fetchAdminCollection,
    fetchAdminEntity,
    fetchAllAdminCollection,
    updateAdminEntity,
} from "./content-type-admin-api";

function errorMessage(error) {
    return error?.response?.data?.error?.message
        || error?.response?.data?.error
        || error?.message
        || "content-type-request-error";
}

function makeQueryKey(params, all) {
    return JSON.stringify({params: params || {}, all: Boolean(all)});
}

export function useContentTypeData(uid) {
    const dispatch = useDispatch();
    const entry = useSelector((state) => selectContentTypeEntry(state, uid));
    const entryRef = useRef(entry);
    entryRef.current = entry;

    const load = useCallback(async (params = {}, options = {}) => {
        const queryKey = makeQueryKey(params, options.all);
        dispatch(contentTypeRequestStarted({uid, queryKey}));
        try {
            const result = options.all
                ? await fetchAllAdminCollection(uid, params)
                : await fetchAdminCollection(uid, params);
            dispatch(contentTypeRequestSucceeded({uid, queryKey, ...result}));
            return result;
        } catch (error) {
            dispatch(contentTypeRequestFailed({uid, error: errorMessage(error)}));
            throw error;
        }
    }, [dispatch, uid]);

    const ensure = useCallback(async (params = {}, options = {}) => {
        const current = entryRef.current;
        const queryKey = makeQueryKey(params, options.all);
        if (!options.force && current.status === "succeeded" && current.queryKey === queryKey) {
            return {items: current.items, total: current.total};
        }
        if (!options.force && current.status === "loading" && current.queryKey === queryKey) {
            return {items: current.items, total: current.total};
        }
        return load(params, options);
    }, [load]);

    const reload = useCallback(
        (params = {}, options = {}) => load(params, {...options, force: true}),
        [load]
    );

    const getOne = useCallback(async (documentId) => {
        const item = await fetchAdminEntity(uid, documentId);
        if (item) dispatch(upsertContentTypeItem({uid, item}));
        return item;
    }, [dispatch, uid]);

    const create = useCallback(async (payload) => {
        const item = await createAdminEntity(uid, payload);
        if (item) dispatch(upsertContentTypeItem({uid, item}));
        return item;
    }, [dispatch, uid]);

    const update = useCallback(async (documentId, payload) => {
        const item = await updateAdminEntity(uid, documentId, payload);
        if (item) dispatch(upsertContentTypeItem({uid, item}));
        return item;
    }, [dispatch, uid]);

    const remove = useCallback(async (documentId) => {
        const item = await deleteAdminEntity(uid, documentId);
        dispatch(removeContentTypeItem({uid, documentId}));
        return item;
    }, [dispatch, uid]);

    const invalidate = useCallback(() => dispatch(invalidateContentType(uid)), [dispatch, uid]);

    return {
        ...entry,
        loading: entry.status === "loading",
        load,
        ensure,
        reload,
        getOne,
        create,
        update,
        remove,
        invalidate,
    };
}
